import { useEffect, useState } from "react";
import { useDocumentGeneratorStore } from "../../stores/documentGeneratorStore";
import { useRecommendationStore } from "../../stores/recommendationStore";
import { fetchCatalogTemplates } from "../../services/catalogApiService";
import { useAuth } from "hooks/useAuth";
import { Dropdown } from "components";

// Компонент кнопки генерації документів (цінників та каталогу)
const RecommendationPricetagButton = ({ isEmpty }) => {
  // Перевірка дозволів користувача
  const { user, isAllowed } = useAuth();
  const canGeneratePricetags = isAllowed("pricetags:generate");
  // У кожного шаблону каталогу свій дозвіл (catalog:generate-*), тому не перелічуємо
  // їх тут: бекенд повертає лише доступні користувачу шаблони
  const canGenerateCatalog = (user?.permissions || []).some((permission) =>
    permission.startsWith("catalog:generate-"),
  );

  // Список шаблонів каталогу (null — ще вантажиться)
  const [templates, setTemplates] = useState(null);

  // Отримуємо дані та дії зі стору генерації документів
  const isSelectionModeActive = useDocumentGeneratorStore(
    (state) => state.isSelectionModeActive,
  );
  const activeDocumentType = useDocumentGeneratorStore(
    (state) => state.activeDocumentType,
  );
  const selectedSkus = useDocumentGeneratorStore((state) => state.selectedSkus);
  const isLoading = useDocumentGeneratorStore((state) => state.isLoading);
  const toggleSelectionMode = useDocumentGeneratorStore(
    (state) => state.toggleSelectionMode,
  );
  const resetState = useDocumentGeneratorStore((state) => state.resetState);
  const selectAll = useDocumentGeneratorStore((state) => state.selectAll);
  const generateAndDownloadPdf = useDocumentGeneratorStore(
    (state) => state.generateAndDownloadPdf,
  );

  // Отримуємо дані зі стору рекомендацій
  const warehouse_id = useRecommendationStore((state) => state.filters.warehouse_id);
  const stocks = useRecommendationStore((state) => state.stocks);

  // Завантажуємо список шаблонів каталогу
  useEffect(() => {
    if (!canGenerateCatalog) {
      setTemplates([]);
      return;
    }
    let active = true;
    fetchCatalogTemplates()
      .then((data) => active && setTemplates(Array.isArray(data) ? data : []))
      .catch(() => active && setTemplates([]));
    return () => {
      active = false;
    };
  }, [canGenerateCatalog]);

  // --- Обробники подій ---

  // Обробник кліку на кнопку генерації (відправка запиту)
  const handleGenerateClick = () => {
    generateAndDownloadPdf(warehouse_id);
  };

  // Обробник скасування (вихід з режиму вибору та очищення стану)
  const handleCancelClick = () => {
    resetState();
  };

  // Запуск режиму вибору за обраною опцією (цінники або каталог+шаблон)
  const startAction = (value) => {
    if (!value) return;
    if (value === "pricetags") {
      toggleSelectionMode("pricetags");
      return;
    }
    if (value.startsWith("catalog:")) {
      const id = value.slice("catalog:".length);
      const template = (templates || []).find((t) => t.id === id) || null;
      toggleSelectionMode("catalog", template);
    }
  };

  // --- Логіка рендерингу ---

  // Якщо немає жодного дозволу, приховуємо компонент
  if (!canGeneratePricetags && !canGenerateCatalog) {
    return null;
  }

  // Стан 1: Режим вибору товарів активний (показуємо панель "Отримати / Скасувати")
  if (isSelectionModeActive) {
    const isCatalog = activeDocumentType === "catalog";
    const docLabel = isCatalog ? "каталог" : "цінники";

    return (
      <div className="pricetag-button-container">
        <button
          className="bulk-bar__link"
          onClick={() => selectAll(stocks.map((s) => s.article))}
          disabled={isLoading || isEmpty}
        >
          Обрати всі
        </button>

        <button
          className="focus-button"
          onClick={handleGenerateClick}
          disabled={isLoading || isEmpty || selectedSkus.length === 0}
        >
          {isLoading ? "Генерація..." : `Отримати ${docLabel} (${selectedSkus.length})`}
        </button>

        <button
          onClick={handleCancelClick}
          className="focus-button-secondary"
          disabled={isLoading}
        >
          Скасувати
        </button>
      </div>
    );
  }

  // Поки вантажаться шаблони каталогу, а інших опцій немає — показуємо заглушку
  if (canGenerateCatalog && templates === null && !canGeneratePricetags) {
    return (
      <div className="pricetag-button-container">
        <button className="focus-button" disabled>
          Завантаження...
        </button>
      </div>
    );
  }

  // Формуємо опції: цінники + окремий каталог під кожен шаблон
  const options = [];
  if (canGeneratePricetags) {
    options.push({ value: "pricetags", label: "Сформувати цінники" });
  }
  if (canGenerateCatalog && templates) {
    templates.forEach((t) =>
      options.push({ value: `catalog:${t.id}`, label: `Каталог — ${t.name}` }),
    );
  }

  if (options.length === 0) {
    return null;
  }

  // Одна опція — звичайна кнопка
  if (options.length === 1) {
    const [only] = options;
    const label = only.value === "pricetags" ? "Формування цінників" : "Формування каталогу";
    return (
      <div className="pricetag-button-container">
        <button
          className="focus-button"
          onClick={() => startAction(only.value)}
          disabled={isLoading || isEmpty}
        >
          {label}
        </button>
      </div>
    );
  }

  // Кілька опцій — Dropdown для вибору дії
  return (
    <div className="pricetag-button-container">
      <Dropdown
        options={options}
        selected={null}
        onChange={startAction}
        placeholder="Сформувати документи"
        mode="select"
        disabled={isLoading || isEmpty}
      />
    </div>
  );
};

export default RecommendationPricetagButton;
